import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import {
  applySchemeDelegate,
  ColorableComponent,
  getColorOfNearestBackground,
  setCssVariablesDelegate
} from "../../common/colorable-component";
import {
  DropdownLocalization,
  DropdownSchemeDefinition,
  DropdownSchemeName,
  DropdownSize,
  DropdownSortOrder
} from "./constants/dropdown-types";
import {
  DROPDOWN_SCHEME_CONFIG,
  DROPDOWN_SCHEME_MAP,
  DROPDOWN_SIZE_NORMAL,
  DROPDOWN_SORT_ASC,
  DROPDOWN_SORT_DESC,
  DROPDOWN_SORT_MANUAL
} from "./constants/dropdown-constants";
import {
  FormComponent,
  handleCustomValidationDelegate,
  validateDelegate,
  validateRequiredValueDelegate
} from "../../common/form-component";
import { BackgroundColor } from "../background/constants/background-types";
import { KeyIds } from "../../constants/keys";
import { isTouchDevice } from "../../utils/touch-helper";
import { DropdownSchemeConfiguration } from "../../constants/colors";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import pfx from "../../utils/style-class-prefix-helper";
import { setCssVariable } from "../../utils/css-variables";
import { detectSlotChanges } from "../../utils/slot-helper";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import { uniqueId } from "../../utils/unique-id";
import { DROPDOWN_CHEVRON_ICON_NAME, DROPDOWN_CHEVRON_SMALL_ICON_NAME } from "../../constants/global-constants";
import { DROPDOWN_LOCALIZATION } from "./constants/dropdown-i18n";
import sanitizeHtml from "sanitize-html";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 * @slot [unnamed] - Holds any number of eon-ui-dropdown-options
 */
@Component({
  tag: "eon-ui-dropdown",
  styleUrl: "dropdown.scss",
  shadow: true
})
export class Dropdown
  implements
    ColorableComponent<DropdownSchemeConfiguration<DropdownSchemeDefinition>>,
    LocalizableComponent<DropdownLocalization>,
    FormComponent<string, DropdownLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: DropdownSchemeConfiguration<DropdownSchemeDefinition> = DROPDOWN_SCHEME_CONFIG;
  schemeMap = DROPDOWN_SCHEME_MAP;

  localization: Localization<DropdownLocalization> = DROPDOWN_LOCALIZATION;
  currentLocalization: DropdownLocalization;

  /**
   * Array of the Select Options that will be rendered inside the Select Dropdown.
   */
  @State() options: HTMLEonUiDropdownOptionElement[] = [];

  selectLabels: string[] = [];
  select: HTMLSelectElement;
  userInputTimeout: any;
  userInputKeys: string = "";

  private id: string;

  @State() selectItems: any[] = [];
  @State() multiselectSelectedOptions: any[] = [];
  @State() isTouched: boolean;
  @State() usingKeyNavigation: boolean = false;

  /**
   * The component's active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: DropdownSchemeDefinition;

  @State() labelOffset: number = 0;
  @State() activeLabelOffset: number = 0;
  private infoIconComponent: HTMLEonUiInfoIconElement;
  private labelButton: HTMLElement;
  private navigationElementsContainer: HTMLElement;

  /**
   * Whether or not an existing CustomValidator has and error.
   */
  @State() customValidatorHasError: boolean = false;

  /**
   * Whether or not the Dropdownlist is collapsed default is set to true.
   */
  @State() collapsed: boolean = true;

  /**
   * A State that contains the shown option label and its actual value.
   */
  @State() selectedOption: HTMLEonUiDropdownOptionElement;

  /**
   * A State that contains the current focused option.
   */
  @State() focusedOption: HTMLEonUiDropdownOptionElement;

  @State() isInfoIconAvailable: boolean;

  /**
   * Raised when the input is focused.
   */
  @State() isFocus: boolean;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiDropdownElement;

  /**
   * **Internal use only!**
   * &nbsp;<br>
   * Please don't change this.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true }) isFormComponent: boolean = true;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop() scheme: DropdownSchemeName;

  /**
   * Sets the label for the dropdown element.
   * @example <eon-ui-dropdown label="First Name"></eon-ui-input>
   */
  @Prop() label: string;

  /**
   * Defines whether the label should be placed above the component.
   */
  @Prop() labelOutside: boolean = false;

  /**
   * Defines whether the label should be placed before the component.
   */
  @Prop() labelBefore: boolean = false;

  /**
   * The name Attribute is a string that refers to the native name attribute of an HtmlInputElement.
   */
  @Prop() name: string;

  /**
   * Set to true if the input is required.
   */
  @Prop() required: boolean;

  /**
   * Set to true if the Input is in info state and the info message should be displayed.
   */
  @Prop() info: boolean = false;

  /**
   * Sets the text of the info message.
   */
  @Prop() infoMessage: string;

  /**
   * Whether or not the dropdown is disabled.
   */
  @Prop({ reflect: true, mutable: true }) disabled: boolean = false;

  /**
   * Set to true if the Dropdown is in success state and the success message should be displayed.
   * @example <eon-ui-dropdown success="true"></eon-ui-dropdown>
   */
  @Prop() success: boolean = false;

  /**
   * Success message to be displayed
   */
  @Prop() successMessage: string;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Whether or not the dropdown is invalid. If the dropdown is invalid, the error message is displayed.
   * Otherwise, the error message is hidden.
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean = false;

  /**
   * Reflects the native html value attribute.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) value: string;

  /**
   * Property  to store the formatted value
   */
  @Prop({ reflect: true, mutable: true }) formattedValue: string = "";

  /**
   * **Deprecated! Use value attribute instead.**
   * &nbsp;<br>
   * The initial selected value.
   */
  @Prop() selected: string;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * The error message that is displayed if the dropdown is invalid. Is listed below the label and sublabel. Hidden (display: none) otherwise.
   * @example <eon-ui-dropdown label="Very important, terms of service" invalid error-message="You need to check this">
   */
  @Prop({ mutable: true }) errorMessage: string;

  /**
   * An empty Placeholder for an optional custom Error Message for CustomValidation.
   * @example <eon-ui-dropdown custom-error-message="this is an custom error message"></eon-ui-dropdown>
   */
  @Prop({ mutable: true }) customErrorMessage: string;

  /**
   * A custom message to show when the required field has no value
   */
  @Prop() customRequiredMessage: string;

  /**
   * Defines the height of the drop down option list.
   * @example "200px", "100px", "auto".
   * If not set, height sticks to the max-height which is calc(100vh - 96px)
   */
  @Prop() height: string;

  /**
   * Set the size of the component
   */
  @Prop() size: DropdownSize = DROPDOWN_SIZE_NORMAL;

  /**
   * Multiselect dropdown functionality. If active, checkboxes are added to the options so that multiple options can be selected.
   */
  @Prop() multiselect: boolean = false;
  /**
   *  Sort the option list and selected items in the Multiselect dropdown.
   */

  @Prop() sortOrder: DropdownSortOrder = DROPDOWN_SORT_MANUAL;

  /**
   * Specifies whether the items that have been selected should appear as chips beneath the dropdown. This property can be used for MultiSelect Dropdown.
   */
  @Prop() showSelectedItems: boolean = false;

  /**
   * Raised when the input has changed. Will emit the current value of the Input field.
   */
  @Event() valueChanged: EventEmitter<string>;

  /**
   * Raised when the dropdown has been loaded.
   */
  @Event() dropdownLoad: EventEmitter<string>;

  /**
   * Raised when the popup has been opened.
   */
  @Event() dropdownOpened: EventEmitter<string>;

  /**
   * Raised when the popup has been closed.
   */
  @Event() dropdownClosed: EventEmitter<string>;

  /**
   * Raised when the input is focused.
   */
  @Event() inputFocus: EventEmitter<FocusEvent>;

  /**
   * Raised when the input is losing focus.
   */
  @Event() inputBlur: EventEmitter<FocusEvent>;

  /**
   * Saves the given error message and assumes that the component is invalid if any error message is provided.
   * @param component The FormComponent that should handle the custom validation.
   * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
   */
  @Method()
  async handleCustomValidation(errorMessage: string) {
    await handleCustomValidationDelegate(this, errorMessage);
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * Validates that the component has a value if the required attribute is set.
   */
  async validateRequired(): Promise<boolean> {
    return await validateRequiredValueDelegate(this);
  }

  /**
   * Combines the result of the internal required validator and the custom validator.
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Watch("required")
  @Method()
  async validate(): Promise<boolean> {
    return await validateDelegate(this);
  }

  /**
   * Update custom-required-message
   */
  @Watch("customRequiredMessage")
  setCustomRequiredMessage() {
    this.validate();
  }

  @Listen("click", { target: "document" })
  onDocumentClick(event) {
    if (this.multiselect && event.target.tagName === "EON-UI-DROPDOWN-OPTION") {
    } else {
      this.closeDropdownEvent(event);
    }
  }

  componentWillLoad() {
    this.id = this.hostElement.id || uniqueId();
    this.options = Array.from(
      getNestedChildrenOf(
        this.hostElement,
        "eon-ui-dropdown-option"
      ) as HTMLCollectionOf<HTMLEonUiDropdownOptionElement>
    );
    this.createOptionsLabelSet();
    if (isTouchDevice()) this.initTouchDropdown();
    this.applyScheme();
    this.passAttributesToOptions();
    this.setCurrentLocalization();
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
    this.infoIconComponent = this.hostElement.querySelector("eon-ui-info-icon");
    !!this.infoIconComponent && this.initInfoIconEventListenerForCollapsing();
  }

  componentDidLoad() {
    this.initDropDown();

    detectSlotChanges(() => {
      const dropdownOptions = Array.from(
        getNestedChildrenOf(
          this.hostElement,
          "eon-ui-dropdown-option"
        ) as HTMLCollectionOf<HTMLEonUiDropdownOptionElement>
      );
      this.options = this.sorting(dropdownOptions, this.sortOrder);
      this.createOptionsLabelSet();
      setTimeout(() => {
        this.initDropDown();
        if (isTouchDevice()) this.initTouchDropdown();
      }, 1000);
    }, this.hostElement);

    this.setCssVariables();
    this.sortOptions();
    this.delegateInputInnerOffsetVariables();
    this.dropdownLoad.emit();
  }

  initInfoIconEventListenerForCollapsing() {
    this.infoIconComponent.addEventListener("infoIconClick", () => {
      this.hideOptionList();
    });
  }

  @Watch("size")
  passAttributesToOptions() {
    this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
      option.setAttribute("size", this.size);
    });
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
    this.setLabelBackgroundColor();
  }

  setLabelBackgroundColor() {
    const backgroundColor = getColorOfNearestBackground(this.hostElement);
    this.hostElement.style.setProperty(`--currentBackgroundColor`, `var(--${backgroundColor})`);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  private initDropDown() {
    this.options.forEach((option: HTMLEonUiDropdownOptionElement, index) => {
      option.id = `${this.id}-${index}`;
      option.onclick = (event) => {
        if (event.target !== (document.querySelector("eon-ui-info-icon") || document.querySelector("eon-ui-modal"))) {
          if (!this.multiselect) {
            this.selectOption(event.target as HTMLEonUiDropdownOptionElement);
          } else {
            this.toggleOption(event.target as HTMLEonUiDropdownOptionElement);
          }
        }
      };
    });

    if (!this.multiselect) {
      if (this.selected && !this.value) this.value = this.selected; // Fallback for deprectated this.selected.
    } else {
      this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
        option.multiselect = true;
      });
      this.initMultiSelectSelectedOptionCollector();
    }

    if (this.value) this.applyPreselection();
  }

  private initTouchDropdown() {
    let tempSelectItems: any[] = [];

    if (this.label) {
      let tempSelectLabelItem = {
        value: "",
        label: this.label,
        selected: !this.value ? true : false,
        innerText: this.label,
        disabled: true
      };

      tempSelectItems.push(tempSelectLabelItem);
    }

    this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
      let optionElement = {
        value: option.value,
        label: option.label,
        selected: this.value && this.value === option.value ? true : false,
        innerText: option.label,
        disabled: option.disabled
      };

      tempSelectItems.push(optionElement);
    });

    this.selectItems = tempSelectItems;
  }

  private initMultiSelectSelectedOptionCollector() {
    const optionsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          (mutation.target as HTMLElement).tagName === "EON-UI-DROPDOWN-OPTION" &&
          mutation.attributeName === "selected"
        ) {
          this.multiselectSelectedOptions = [];
          this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
            if (option.selected) this.multiselectSelectedOptions.push(option);
          });
          this.updateMultiselectValue();
        }
      });
    });

    optionsObserver.observe(this.hostElement, {
      subtree: true,
      attributes: true //configure it to listen to attribute changes
    });
  }

  private deselectAllMultiselectValues() {
    this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
      option.style.order = "0";
      option.selected = false;
    });
    this.sortOptions();
  }

  private updateMultiselectValue() {
    let multiselectValueArr = [];
    let multiselectFormattedValueArr = [];

    this.multiselectSelectedOptions.forEach((option: HTMLEonUiDropdownOptionElement) => {
      multiselectValueArr.push(option.value);
      multiselectFormattedValueArr.push(option.label);
    });

    multiselectValueArr.length > 0 ? (this.isTouched = true) : (this.isTouched = false);

    this.value = multiselectValueArr.join(", ");
    this.formattedValue = multiselectFormattedValueArr.join("; ");

    this.validate();
  }

  @Watch("collapsed")
  datePickerVisibilityEventHandler() {
    if (this.collapsed) {
      this.dropdownClosed.emit();
    } else {
      this.dropdownOpened.emit();
    }
  }

  @Watch("value")
  emitValueChangedEvent() {
    this.valueChanged.emit(this.value);
  }

  @Watch("value")
  updateValue() {
    if (!this.multiselect) {
      this.selectOptionByValue(this.value);
    }
  }

  private applyPreselection() {
    if (!this.multiselect) {
      this.selectOptionByValue(this.value);
    } else {
      this.selectMultiselectOptionsByValue(this.value);
    }
  }

  private selectMultiselectOptionsByValue(value: string) {
    let preselectedOptionValues = value.split(",");

    preselectedOptionValues.forEach((value) => {
      this.options.forEach((option) => {
        if (option.value === value.trim()) option.selected = true;
      });
    });

    this.validate();
  }

  private selectOptionByValue(value: string) {
    const newSelectedOption: HTMLEonUiDropdownOptionElement = this.options.filter(
      (option) => option.value === value
    )[0];
    if (newSelectedOption) {
      this.options.forEach((option) => {
        option.removeAttribute("selected");
      });
      newSelectedOption.setAttribute("selected", "true");
      this.selectedOption = newSelectedOption;
      this.focusOptionByValue(newSelectedOption.value);
      this.value = newSelectedOption.value;
      this.formattedValue = newSelectedOption.label;
      this.validate();
    }
  }

  private selectOption(option?: HTMLEonUiDropdownOptionElement) {
    if (option) {
      // custom select dropdown
      this.selectOptionByValue(option.value);
    } else {
      // native select on e.g. mobile devices
      this.selectOptionByValue(this.select.value);
    }
    this.labelButton.focus();
  }

  private toggleOption(option?: HTMLEonUiDropdownOptionElement) {
    if (option.selected) {
      option.selected = false;
    } else {
      option.selected = true;
    }
  }

  private focusOptionByValue(value) {
    const newFocusedOption: HTMLEonUiDropdownOptionElement = this.options.filter((option) => option.value === value)[0];
    if (newFocusedOption) {
      this.options.forEach((option) => {
        option.removeAttribute("has-focus");
        option.removeAttribute("is-key-selected");
      });
      newFocusedOption.setAttribute("has-focus", "true");
      newFocusedOption.setAttribute("is-key-selected", this.usingKeyNavigation ? "true" : "false");
      newFocusedOption.focus();
      this.focusedOption = newFocusedOption;
    }
  }

  private focusOption(option: HTMLEonUiDropdownOptionElement) {
    this.focusOptionByValue(option.value);
  }

  private showOptionList() {
    if (this.disabled) {
      return;
    }

    this.collapsed = false;
  }

  private hideOptionList() {
    if (this.disabled) {
      return;
    }

    this.collapsed = true;
  }

  private toggleOptionList(event) {
    if (this.disabled) {
      return;
    }
    this.usingKeyNavigation = false;
    this.options.forEach((option) => {
      option.setAttribute("is-key-selected", "false");
    });

    if (event.target.nodeName === "EON-UI-INFO-ICON") {
      return;
    }

    if (this.multiselect && event.target.nodeName === "EON-UI-DROPDOWN-OPTION") {
      return;
    }

    if (this.collapsed) this.sortOptions();

    this.collapsed = !this.collapsed;
  }

  private closeDropdownEvent(e) {
    const srcElement = e.srcElement as HTMLElement;
    let hostElementIsPartOfParentShadowRoot = e.composedPath().includes(this.hostElement);

    if (
      srcElement !== this.hostElement &&
      !hostElementIsPartOfParentShadowRoot &&
      !this.hostElement.contains(srcElement)
    ) {
      this.hideOptionList();
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    let focusedOptionIndex: number;
    this.usingKeyNavigation = true;

    if (this.disabled) {
      return;
    }

    switch (e.key) {
      case KeyIds.SPACE:
      case KeyIds.ENTER:
        e.preventDefault();
        e.stopPropagation();
        if (this.collapsed) {
          if (this.selectedOption) {
            this.selectOption(this.selectedOption);
          }
          this.sortOptions();
          this.showOptionList();
        } else {
          if (!this.multiselect) {
            if (this.focusedOption) {
              this.selectOption(this.focusedOption);
            }
            this.collapsed = true;
          } else {
            if (this.focusedOption) {
              this.toggleOption(this.focusedOption);
            } else {
              this.collapsed = true;
            }
          }
        }
        break;

      case KeyIds.DOWN:
        e.preventDefault();
        if (this.collapsed) {
          if (this.selectedOption) {
            this.focusOption(this.selectedOption);
          } else {
            this.focusOption(this.options[0]);
          }
          this.showOptionList();
        } else {
          focusedOptionIndex = this.options.indexOf(this.focusedOption);
          if (focusedOptionIndex === -1 || focusedOptionIndex === this.options.length - 1) {
            this.focusOption(this.options[0]);
          } else if (focusedOptionIndex < this.options.length - 1) {
            this.focusOption(this.options[focusedOptionIndex + 1]);
          }
        }
        break;

      case KeyIds.UP:
        e.preventDefault();
        if (this.collapsed) {
          if (this.selectedOption) {
            this.focusOption(this.selectedOption);
          } else {
            this.focusOption(this.options[this.options.length - 1]);
          }
          this.showOptionList();
        } else {
          focusedOptionIndex = this.options.indexOf(this.focusedOption);
          if (focusedOptionIndex === -1 || focusedOptionIndex === 0) {
            this.focusOption(this.options[this.options.length - 1]);
          } else if (focusedOptionIndex > 0) {
            this.focusOption(this.options[focusedOptionIndex - 1]);
          }
        }
        break;

      case KeyIds.TAB:
      case KeyIds.ESCAPE:
        this.collapsed = true;
        break;

      case KeyIds.HOME:
        e.preventDefault();
        this.focusOption(this.options[0]);
        break;

      case KeyIds.END:
        e.preventDefault();
        this.focusOption(this.options[this.options.length - 1]);
        break;

      default:
        if (!this.collapsed) {
          this.collectUserInputString(e.key);
          focusedOptionIndex = this.getIndexOfMatchingStringInSetOfStrings(this.userInputKeys, this.selectLabels);
          if (focusedOptionIndex > -1) this.focusOption(this.options[focusedOptionIndex]);
        }
        break;
    }
  }

  private handleMultiselectDeleteKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        this.deselectAllMultiselectValues();
        break;
      case KeyIds.ENTER:
        e.preventDefault();
        break;
    }
  }

  private handleIconName() {
    return this.size === DROPDOWN_SIZE_NORMAL ? DROPDOWN_CHEVRON_ICON_NAME : DROPDOWN_CHEVRON_SMALL_ICON_NAME;
  }

  private delegateInputInnerOffsetVariables() {
    const labelOffset = this.navigationElementsContainer.offsetWidth;
    setCssVariable(this.hostElement as HTMLElement, "--dropdownInnerOffset", `${labelOffset}px`);
  }

  @Watch("options")
  private createOptionsLabelSet() {
    if (this.options) {
      this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
        this.selectLabels.push(option.label ? option.label.toLowerCase() : "");
      });
    }
  }

  private collectUserInputString(key: string) {
    if (this.userInputTimeout) {
      this.userInputTimeout = clearTimeout(this.userInputTimeout);
    }
    this.userInputTimeout = setTimeout(() => {
      this.userInputKeys = "";
    }, 500);
    this.userInputKeys += key;
  }

  private getIndexOfMatchingStringInSetOfStrings(string: string, setOfStrings: string[]) {
    let stringLength = string.length;
    let index = setOfStrings.findIndex((s) => s.substring(0, stringLength).includes(string.toLowerCase()));
    return index;
  }

  private onFocus(event: FocusEvent) {
    this.inputFocus.emit(event);
    this.isFocus = true;
  }

  private onBlur(event: FocusEvent) {
    this.inputBlur.emit(event);
    this.isFocus = false;
  }

  @Watch("sortOrder")
  private sortOptions() {
    let sortedOptions: HTMLEonUiDropdownOptionElement[] = this.sorting(this.options, this.sortOrder);
    sortedOptions.forEach((option: HTMLEonUiDropdownOptionElement, index) => (option.style.order = String(index)));
    if (this.multiselect) {
      let selectedOption = sortedOptions.filter((option) => option.selected);
      let deselectedOption = sortedOptions.filter((option) => !option.selected);

      if (selectedOption.length > 0) {
        sortedOptions = selectedOption.concat(deselectedOption);
        sortedOptions.forEach((sortedOption, index: number) => {
          this.options.forEach((option) => {
            if (sortedOption.value === option.value) option.style.order = String(index);
          });
        });
      }
    }
    this.options = sortedOptions;
  }

  private sorting(options: HTMLEonUiDropdownOptionElement[], order: string) {
    return order === DROPDOWN_SORT_ASC
      ? options.slice().sort((a, b) => (a.label > b.label ? 1 : -1))
      : order === DROPDOWN_SORT_DESC
      ? options.slice().sort((a, b) => (a.label > b.label ? -1 : 1))
      : options;
  }

  private deselectOption(value: string) {
    this.options.forEach((option: HTMLEonUiDropdownOptionElement) => {
      if (option.value === value) option.selected = false;
    });
  }

  private handleDeselectionKeyDown(e: KeyboardEvent, value: string) {
    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        this.deselectOption(value);
        break;
      case KeyIds.ENTER:
        e.preventDefault();
        break;
    }
  }

  render() {
    const infoIconWrapper = () => {
      return (
        <div class={pfx("dropdown-info-icon")}>
          <slot name="info-icon" />
        </div>
      );
    };

    let describedBy = "";
    if (!this.disabled && this.invalid) {
      describedBy = `${this.id}-error`;
    } else if (!this.disabled && this.success && this.successMessage) {
      describedBy = `${this.id}-success`;
    } else if (this.info) {
      describedBy = `${this.id}-info`;
    }

    return (
      <Host class={pfx({ active: (this.isFocus || !this.collapsed) && !this.disabled })}>
        {(this.labelOutside || this.labelBefore) && (
          // eslint-disable-next-line jsx-a11y/label-has-associated-control
          <label
            htmlFor={`${this.name}-${this.id}`}
            id={`${this.hostElement.name}-listbox-label-${this.id}`}
            innerHTML={sanitizeHtml(this.label)}
            class={pfx({
              outside: this.labelOutside,
              before: this.labelBefore,
              [this.size]: true,
              error: this.invalid,
              disabled: this.disabled,
              filled: this.value
            })}
          ></label>
        )}
        {
          // Renders the "custom WebComponent approach" only on non-touch devices.
          // To keep the native behavior on touch devices, a standard <select> will be rendered instead.
          !isTouchDevice() || this.multiselect ? (
            <div
              class={pfx({
                ["dropdown-wrapper"]: true,
                ["touched"]: !!this.selectedOption?.label || this.isTouched,
                [this.size]: true,
                "popup-visible": !this.collapsed,
                disabled: this.disabled,
                success: this.success,
                invalid: this.invalid
              })}
            >
              <button
                id={`${this.hostElement.name}-${this.id}`}
                ref={(el) => (this.labelButton = el as HTMLElement)}
                role="combobox"
                class={pfx({
                  "dropdown-button": true
                })}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={this.collapsed ? "false" : "true"}
                aria-controls={`${this.hostElement.name}-${this.id}-listbox`}
                disabled={this.disabled}
                onFocus={(event) => this.onFocus(event)}
                onBlur={(event) => this.onBlur(event)}
                onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
                onClick={(event) => this.toggleOptionList(event)}
                aria-describedby={describedBy}
              >
                {this.multiselect ? (
                  <div class={pfx({ "option-label-wrapper": true })}>
                    {this.multiselectSelectedOptions.length > 0 && (
                      <eon-ui-chip
                        label={`${this.multiselectSelectedOptions.length} ${this.currentLocalization.selected}`}
                        scheme={
                          !this.collapsed || this.isFocus
                            ? this.activeScheme.chipSchemeFocus
                            : this.activeScheme.chipScheme
                        }
                        onChipClick={() => this.deselectAllMultiselectValues()}
                        onKeyDown={(e: KeyboardEvent) => this.handleMultiselectDeleteKeyDown(e)}
                        size={this.size === "small" ? "tiny" : "normal"}
                        tabIndex={this.disabled ? -1 : null}
                        disabled={this.disabled}
                        onFocus={(event) => this.onFocus(event)}
                        onBlur={(event) => this.onBlur(event)}
                      ></eon-ui-chip>
                    )}
                  </div>
                ) : (
                  <div class={pfx({ "option-label-wrapper": true })}>{this.selectedOption?.label}</div>
                )}
                <div
                  id={`${this.hostElement.name}-${this.id}-listbox`}
                  role="listbox"
                  class={pfx({
                    "option-list": true,
                    "is-collapsed": this.collapsed
                  })}
                  tabIndex={-1}
                  style={{ maxHeight: this.height }}
                >
                  <slot />
                </div>
              </button>
              {!this.labelOutside && !this.labelBefore && this.label && (
                //eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label
                  id={`${this.hostElement.name}-listbox-label-${this.id}`}
                  htmlFor={`${this.name}-${this.id}`}
                  class={pfx({
                    filled: this.value
                  })}
                  innerHTML={sanitizeHtml(this.label)}
                ></label>
              )}
              {!this.labelOutside && !this.labelBefore && <div class={pfx("label-placeholder")}>{this.label}</div>}
              <div
                class={pfx({
                  "navigation-elements": true,
                  "contains-info-icon": this.isInfoIconAvailable
                })}
                ref={(el) => (this.navigationElementsContainer = el as HTMLElement)}
              >
                {this.isInfoIconAvailable && infoIconWrapper()}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div class={pfx({ ["icon-background"]: true })} onClick={(event) => this.toggleOptionList(event)}>
                  <div class={pfx({ ["icon-button"]: true })}>
                    <eon-ui-icon
                      aria-label={this.handleIconName()}
                      class={pfx({
                        icon: true
                      })}
                      name={this.handleIconName()}
                      noSpace={true}
                    ></eon-ui-icon>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              class={pfx({
                ["dropdown-wrapper"]: true,
                ["touched"]: !!this.selectedOption?.label || this.isTouched,
                [this.size]: true,
                "popup-visible": !this.collapsed,
                disabled: this.disabled,
                invalid: this.invalid,
                success: this.success
              })}
            >
              <select
                ref={(el) => (this.select = el as HTMLSelectElement)}
                onChange={() => this.selectOption()}
                onFocus={(event) => this.inputFocus.emit(event)}
                onBlur={(event) => this.inputBlur.emit(event)}
                disabled={this.disabled}
                name={this.name}
                aria-describedby={describedBy}
              >
                {this.selectItems.map((option) => (
                  <option
                    value={option.value}
                    label={option.label}
                    selected={option.selected}
                    disabled={option.disabled}
                  >
                    {option.innerText}
                  </option>
                ))}
              </select>
              <div class={pfx("mobile-options")}>
                <slot />
              </div>
              <div
                class={pfx({
                  "dropdown-button": true
                })}
              >
                <div class={pfx({ "option-label-wrapper": true })}>{this.selectedOption?.label}</div>
              </div>
              {!this.labelOutside && !this.labelBefore && (
                //eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label
                  innerHTML={sanitizeHtml(this.label)}
                  class={pfx({ test: true, disabled: this.disabled })}
                ></label>
              )}
              {!this.labelOutside && !this.labelBefore && <div class={pfx("label-placeholder")}>{this.label}</div>}
              <div
                class={pfx({
                  "navigation-elements": true,
                  "contains-info-icon": this.isInfoIconAvailable
                })}
                ref={(el) => (this.navigationElementsContainer = el as HTMLElement)}
              >
                {this.isInfoIconAvailable && infoIconWrapper()}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div class={pfx({ ["icon-background"]: true })} onClick={(event) => this.toggleOptionList(event)}>
                  <div class={pfx({ ["icon-button"]: true })}>
                    <eon-ui-icon
                      aria-label={this.handleIconName()}
                      class={pfx({
                        icon: true
                      })}
                      name={this.handleIconName()}
                      noSpace={true}
                    ></eon-ui-icon>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        <div class={pfx("message-wrapper")}>
          {!this.disabled && this.invalid && (
            <eon-ui-form-message
              id={`${this.id}-error`}
              class={pfx("error-message")}
              type="error"
              message={this.errorMessage}
            ></eon-ui-form-message>
          )}
          {this.info && (
            <eon-ui-form-message
              id={`${this.id}-info`}
              class={pfx("info-message")}
              type="info"
              message={this.infoMessage}
            ></eon-ui-form-message>
          )}
          {!this.disabled && this.success && this.successMessage && (
            <eon-ui-form-message
              id={`${this.id}-success`}
              class={pfx("success-message")}
              type="success"
              message={this.successMessage}
            ></eon-ui-form-message>
          )}
        </div>

        {this.showSelectedItems && this.multiselect && this.multiselectSelectedOptions.length > 0 && (
          <div class={pfx("selected-options")}>
            {this.sorting(this.multiselectSelectedOptions, this.sortOrder).map((option) => (
              <eon-ui-chip
                label={option.label}
                onChipClick={() => this.deselectOption(option.value)}
                onKeyDown={(e: KeyboardEvent) => this.handleDeselectionKeyDown(e, option.value)}
                size={this.size === "small" ? "tiny" : "normal"}
                rank="secondary"
                disabled={this.disabled}
              ></eon-ui-chip>
            ))}
          </div>
        )}
      </Host>
    );
  }
}
